import React from "react";
import { AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";

/**
 * App-level error boundary. Catches render errors anywhere inside its
 * subtree and shows a recovery card instead of letting React unmount
 * the whole app (which on mobile presents as the screen "going blank
 * and you must close").
 *
 * Locked 2026-02 #64 — wrap every Outlet so a Deep Research / Settings
 * / any other page crash is recoverable in-place via the Reload or
 * Back buttons rather than forcing the user to kill the PWA.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Log to console so developers can pick it up via remote debugging
    // (Safari Web Inspector / Chrome devtools) on mobile.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    // [Iter 339] Allow callers to inject a tiny fallback UI (e.g. the
    // UploadDock crash fallback) instead of the full-page recovery
    // card. Falls back to the recovery card if none supplied.
    if (this.props.fallback) {
      const fb = this.props.fallback;
      return typeof fb === "function"
        ? fb({ error: this.state.error, reset: this.reset })
        : fb;
    }
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div
        className="hv-error-boundary max-w-xl mx-auto my-8 p-5 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-100"
        data-testid="error-boundary"
        role="alert"
      >
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-rose-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="hv-error-title text-sm font-bold text-rose-100">This page hit a snag</div>
            <div className="hv-error-sub text-xs text-rose-200/80 mt-0.5">
              We caught the error so the rest of the app stays alive. You can
              reload this view or go back.
            </div>
          </div>
        </div>
        <pre className="hv-error-msg text-[11px] font-mono bg-black/30 border border-rose-500/30 rounded-md p-3 overflow-auto max-h-40 text-rose-100/90" data-testid="error-boundary-message">
          {msg}
        </pre>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={this.reset}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="error-boundary-retry"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="error-boundary-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Go back
          </button>
          <button
            type="button"
            onClick={() => { window.location.href = "/"; }}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="error-boundary-home"
          >
            Home
          </button>
        </div>
      </div>
    );
  }
}
